/**
 * Wire types for the `maintenance` module — derived field-by-field from
 * `serializeMaintenanceRequest` / `serializeMaintenanceRequestRow` /
 * `serializeMaintenanceImage` (refactor/backend/src/modules/maintenance/serializers.ts).
 * snake_case verbatim, no camelCase mapping layer — see the FIELD-NAMING
 * DECISION note in src/types/user.ts.
 */

/** Django `maintenance.models.RequestCategory`. */
export const REQUEST_CATEGORIES = [
  'PLUMBING',
  'ELECTRICAL',
  'HVAC',
  'APPLIANCE',
  'STRUCTURAL',
  'PEST_CONTROL',
  'OTHER',
] as const;
export type RequestCategory = (typeof REQUEST_CATEGORIES)[number];

/** Django `maintenance.models.RequestPriority`. */
export const REQUEST_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const;
export type RequestPriority = (typeof REQUEST_PRIORITIES)[number];

/** Django `maintenance.models.RequestStatus`. */
export const REQUEST_STATUSES = ['NEW', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

/** `MaintenanceImageSerializer` — backend serializers.ts:61-75. */
export interface MaintenanceImage {
  id: number;
  request: number;
  image: string | null;
  caption: string;
  uploaded_at: string;
}

/**
 * `MaintenanceRequestSerializer` — backend serializers.ts:78-105.
 *
 * `agent_name` is `null` (never omitted) when `assigned_agent` is null — this
 * port's documented divergence from DRF's `SkipField` behaviour on a dotted
 * source; see the file-level comment in the backend serializer.
 */
export interface MaintenanceRequest {
  id: number;
  unit: number;
  unit_number: string;
  property_name: string;
  property_id: number;
  tenant: number;
  tenant_name: string;
  assigned_agent: number | null;
  agent_name: string | null;
  category: RequestCategory;
  priority: RequestPriority;
  status: RequestStatus;
  title: string;
  description: string;
  completion_notes: string;
  images: MaintenanceImage[];
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

/** `MaintenanceListSerializer` — backend serializers.ts:108-123. */
export interface MaintenanceRequestRow {
  id: number;
  title: string;
  tenant_name: string;
  unit_number: string;
  property_name: string;
  property_id: number;
  category: RequestCategory;
  priority: RequestPriority;
  status: RequestStatus;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Write requests
// ---------------------------------------------------------------------------

/**
 * `maintenanceRequestCreateSchema` on write (serializers.ts:138-148).
 * `read_only_fields = ['id', 'tenant', 'created_at', 'updated_at']` — the
 * caller is always the tenant server-side, whatever their role.
 */
export interface MaintenanceRequestCreateRequest {
  unit: number;
  assigned_agent?: number | null | undefined;
  category?: RequestCategory | undefined;
  priority?: RequestPriority | undefined;
  status?: RequestStatus | undefined;
  title: string;
  description: string;
  completion_notes?: string | undefined;
  completed_at?: string | null | undefined;
}

/** `maintenanceRequestPatchSchema` — every field of the create request, optional. */
export type MaintenanceRequestPatchRequest = Partial<MaintenanceRequestCreateRequest>;

/** `POST maintenance/{id}/assign/` body — bespoke shape, not the patch schema. */
export interface AssignMaintenanceRequestRequest {
  agent_id: number;
}

/** `POST maintenance/{id}/complete/` body — bespoke shape, not the patch schema. */
export interface CompleteMaintenanceRequestRequest {
  completion_notes?: string | undefined;
}
